import {
  FormsConfigurationError,
  getFormsApiConfig,
} from '../config/api'

import {
  FORMS_SITE_NAME,
} from '../config/formsSite'

const FORMS_SITE_ID =
  'diamond-salon'

type SecurityFields = {
  website: string
  recaptchaToken: string
  consent: boolean
}

export type ContactFormPayload =
  SecurityFields & {
    formType: 'contact'
    websiteName: typeof FORMS_SITE_NAME
    name: string
    email: string
    phone: string
    subject: string
    message: string
    pageUrl: string
    submittedAt: string
  }

export type InquiryFormPayload =
  SecurityFields & {
    formType: 'inquiry'
    websiteName: typeof FORMS_SITE_NAME
    name: string
    email: string
    phone: string
    serviceType: string
    otherServiceDescription: string
    preferredContactMethod: string
    message: string
    pageUrl: string
    submittedAt: string
  }

type ApiSuccess = {
  ok: true
  message?: string
  requestId?: string
}

type ApiErrorBody = {
  ok?: false

  error?: {
    code?: string
    message?: string
  }

  message?: string

  fieldErrors?: Record<
    string,
    string
  >
}

export class FormsApiError extends Error {
  constructor(
    message: string,
    public readonly userMessage: string,
    public readonly status?: number,
    public readonly fieldErrors?: Record<
      string,
      string
    >,
  ) {
    super(message)

    this.name =
      'FormsApiError'
  }
}

async function readJson(
  response: Response,
): Promise<
  ApiSuccess |
  ApiErrorBody
> {
  const contentType =
    response.headers.get(
      'content-type',
    ) ?? ''

  if (
    !contentType
      .toLowerCase()
      .includes(
        'application/json',
      )
  ) {
    throw new FormsApiError(
      `Forms API returned an unexpected content type (${contentType || 'missing'}).`,
      'We could not confirm your submission. Please try again or contact the salon directly.',
      response.status,
    )
  }

  try {
    const body =
      await response.json()

    return body as
      | ApiSuccess
      | ApiErrorBody
  } catch {
    throw new FormsApiError(
      'Forms API returned invalid JSON.',
      'We could not confirm your submission. Please try again or contact the salon directly.',
      response.status,
    )
  }
}

async function submit<
  TPayload extends
    | ContactFormPayload
    | InquiryFormPayload,
>(
  url: string,
  payload: TPayload,
) {
  if (
    typeof navigator !==
      'undefined' &&
    !navigator.onLine
  ) {
    throw new FormsApiError(
      'The browser is offline.',
      'You appear to be offline. Reconnect and try again; your information is still here.',
    )
  }

  const controller =
    new AbortController()

  let timedOut =
    false

  const timeoutId =
    window.setTimeout(
      () => {
        timedOut =
          true

        controller.abort()
      },
      15_000,
    )

  try {
    const response =
      await fetch(
        url,
        {
          method:
            'POST',

          headers: {
            'Content-Type':
              'application/json',
          },

          body:
            JSON.stringify({
              site:
                FORMS_SITE_ID,

              ...payload,
            }),

          signal:
            controller.signal,
        },
      )

    if (
      response.status ===
      429
    ) {
      throw new FormsApiError(
        'The forms API rate limit was reached.',
        'Too many attempts were received. Please wait a few minutes and try again.',
        429,
      )
    }

    if (
      response.status ===
      409
    ) {
      throw new FormsApiError(
        'The forms API rejected a duplicate submission.',
        'This message was already received. Please wait before sending it again.',
        409,
      )
    }

    const body =
      await readJson(
        response,
      )

    if (
      !response.ok
    ) {
      const errorBody =
        body as ApiErrorBody

      const isValidationError =
        response.status ===
          400 ||
        response.status ===
          422

      throw new FormsApiError(
        `Forms API rejected the request with status ${response.status}.`,

        isValidationError
          ? 'Please review the highlighted information and try again.'
          : 'The salon form service is temporarily unavailable. Please try again later.',

        response.status,

        errorBody
          .fieldErrors,
      )
    }

    if (
      body.ok !==
      true
    ) {
      throw new FormsApiError(
        'Forms API did not explicitly confirm success.',
        'We could not confirm your submission. Please try again or contact the salon directly.',
        response.status,
      )
    }

    return body
  } catch (error) {
    if (timedOut) {
      throw new FormsApiError(
        'The forms API request timed out.',
        'The form service took too long to respond. Your information is still here; please try again.',
      )
    }

    if (
      error instanceof
      FormsApiError
    ) {
      throw error
    }

    throw new FormsApiError(
      'The forms API request failed.',
      'We could not reach the salon form service. Please check your connection and try again.',
    )
  } finally {
    window.clearTimeout(
      timeoutId,
    )
  }
}

export function submitContactForm(
  payload:
    ContactFormPayload,
) {
  try {
    return submit(
      getFormsApiConfig()
        .contactUrl,

      payload,
    )
  } catch (error) {
    if (
      error instanceof
      FormsConfigurationError
    ) {
      return Promise.reject(
        error,
      )
    }

    throw error
  }
}

export function submitInquiryForm(
  payload:
    InquiryFormPayload,
) {
  try {
    return submit(
      getFormsApiConfig()
        .inquiryUrl,

      payload,
    )
  } catch (error) {
    if (
      error instanceof
      FormsConfigurationError
    ) {
      return Promise.reject(
        error,
      )
    }

    throw error
  }
}